
import {
  format,
  add,
  startOfMonth,
  parse,
  eachDayOfInterval,
  endOfMonth
} from 'date-fns'
import { computed, defineComponent, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { formatDate, normalizeArray } from '@/utils'
import { useApi } from '@/api'
import { showPeriodsInMonthlyView } from '@/state/monthly-view'
import { showDeactivatedUsers } from '@/state/deactivated-users'
import TimeSwitcher from '@/components/time-switcher.vue'
import UserRow from '@/components/calendars/team-monthly-user-row.vue'
import UserInTable from '@/components/user-in-table.vue'

const USER_LABEL_WIDTH = 220

export default defineComponent({
  components: { TimeSwitcher, UserRow, UserInTable },
  setup() {
    /**
     * Day width
     */
    const dayWidth = computed(() =>
      showPeriodsInMonthlyView.value ? 120 : 100
    )

    /**
     * Current month state
     */
    const route = useRoute()
    const routeMonth = `${route.params.formattedDate}`
    const currentDate = ref(
      /^\d{4}-\d\d-\d\d$/.test(routeMonth)
        ? parse(routeMonth, 'yyyy-MM-dd', new Date())
        : new Date()
    )
    const routeIds = route.query.teamsIds
    const teamsIds = normalizeArray(routeIds).map(Number)

    /**
     * Routing
     */
    const router = useRouter()
    function nextMonth() {
      router.push({
        ...route,
        params: {
          formattedDate: formatDate(
            startOfMonth(add(currentDate.value, { months: 1 }))
          )
        }
      })
    }

    function previousMonth() {
      router.push({
        ...route,
        params: {
          formattedDate: formatDate(
            endOfMonth(add(currentDate.value, { months: -1 }))
          )
        }
      })
    }

    /**
     * Month days
     */
    const firstDay = startOfMonth(currentDate.value)
    const lastDay = add(firstDay, { months: 1, days: -1 })
    const interval = { start: firstDay, end: lastDay }
    const monthDaysList = ref(eachDayOfInterval(interval))

    /**
     * Api call
     */
    const usersRes = useApi(api =>
      api.timeline.monthly(formatDate(currentDate.value), {
        teamsIds,
        includeDeactivated: showDeactivatedUsers.value
      })
    )
    const isLoading = usersRes.isLoading
    const users = usersRes.data
    const refresh = usersRes.refresh

    /**
     * Export CSV
     */
    function calcWorkHours(periodsStr: string): number {
      if (!periodsStr) return 0

      const minutesOfWork = periodsStr
        .split(',')
        .map(period => {
          const [start, end] = period.split('-')
          const [startHours, startMinutes] = start.split(':').map(Number)
          const [endHours, endMinutes] = end.split(':').map(Number)

          return (endHours - startHours) * 60 + endMinutes - startMinutes
        })
        .reduce((a, b) => a + b, 0)

      let hours = Math.floor(minutesOfWork / 60)
      const minutes = minutesOfWork % 60

      if (minutes >= 40) {
        hours += 1
      } else if (minutes >= 20) {
        hours += 0.5
      }

      return hours
    }

    function escapeCsvValue(value: string | number): string {
      const stringValue = `${value}`
      if (/[",\n]/.test(stringValue)) {
        return `"${stringValue.replace(/"/g, '""')}"`
      }
      return stringValue
    }

    function exportCsv(): void {
      if (!users.value) return

      const header = [
        'osoba',
        ...monthDaysList.value.map(day => format(day, 'dd.MM'))
      ]

      const rows = users.value.map(user => {
        const daysByDate = new Map(
          user.days.map(day => [day.formattedDate, day])
        )

        const values = monthDaysList.value.map(day => {
          const formattedDate = format(day, 'yyyy-MM-dd')
          const dayInfo = daysByDate.get(formattedDate)

          if (!dayInfo) return 0
          if (!dayInfo.businessDay) return 'X'

          return calcWorkHours(dayInfo.periods)
        })

        return [`${user.firstName} ${user.lastName}`, ...values]
      })

      const csvContent =
        [header, ...rows]
          .map(row => row.map(escapeCsvValue).join(','))
          .join('\n') + '\n'

      const blob = new Blob([`\ufeff${csvContent}`], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute(
        'download',
        `monthly-availability-${format(currentDate.value, 'yyyy-MM')}.csv`
      )
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    }

    /**
     * Scroll view to current date
     */
    const tableContainer = ref<HTMLDivElement>()
    const dayOfTheMonth = parseInt(format(currentDate.value, 'd'))
    const scrollShift = (dayOfTheMonth - 2) * dayWidth.value

    watch(isLoading, async () => {
      await nextTick()
      if (tableContainer.value) {
        tableContainer.value.scrollLeft = scrollShift
      }
    })

    watch(showDeactivatedUsers, () => {
      refresh()
    })

    /**
     * Table width
     */
    const tableStyle = computed(() => {
      return {
        width: `${
          USER_LABEL_WIDTH + monthDaysList.value.length * dayWidth.value
        }px`
      }
    })

    /**
     * Return
     */
    return {
      currentDate,
      format,
      exportCsv,
      nextMonth,
      previousMonth,
      isLoading,
      monthDaysList,
      users,
      tableContainer,
      tableStyle,
      dayWidth,
      USER_LABEL_WIDTH,
      showPeriodsInMonthlyView,
      showDeactivatedUsers
    }
  }
})
